import { useState, useEffect, useCallback } from "react";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Col,
  Form,
  Row,
} from "Components/Core";
import {
  GeminiPromptObjectType,
  ObjectiveFieldType,
} from "Interfaces/Pages/GeminiPromptsInterface";
import {
  updateGeminiPrompt,
  createGeminiPrompt,
  getMasterSportsExcluding,
  getGeminiObjectiveFieldUnits,
} from "Api/Pages/GeminiPromptsApi";
import { setToastMessage } from "App/commonReducer";
import { useAppDispatch } from "App/hooks";
import { SUCCESS } from "App/constants";
import { SportObjectType } from "Interfaces/Pages/SportsInterface";
import { ToggleSwitch } from "Components/Shared";

interface EditGeminiPromptModalProps {
  isModalVisible: boolean;
  prompt: GeminiPromptObjectType | null;
  existingPrompts: GeminiPromptObjectType[];
  toggleModal: () => void;
  onUpdateSuccess: () => void;
}

const BLANK_OBJECTIVE_FIELD: ObjectiveFieldType = {
  title: "",
  unit: "",
  description: "",
};

const EditGeminiPromptModal: React.FC<EditGeminiPromptModalProps> = ({
  isModalVisible,
  prompt,
  existingPrompts,
  toggleModal,
  onUpdateSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [promptText, setPromptText] = useState<string>("");
  const [promptCategory, setPromptCategory] = useState<"sport" | "exercise">(
    "sport"
  );
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [exerciseType, setExerciseType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [availableSports, setAvailableSports] = useState<SportObjectType[]>([]);
  const [objectiveFields, setObjectiveFields] = useState<ObjectiveFieldType[]>([
    { ...BLANK_OBJECTIVE_FIELD },
  ]);
  const [availableUnits, setAvailableUnits] = useState<string[]>([]);

  const isEditMode = !!prompt;

  const fetchAvailableSports = useCallback(async () => {
    const excludeIds = existingPrompts
      .filter((p) => p.master_sport_id !== null)
      .map((p) => p.master_sport_id as number);
    const sports = await getMasterSportsExcluding(excludeIds);
    setAvailableSports(sports);
  }, [existingPrompts]);

  const fetchUnits = useCallback(async () => {
    const units = await getGeminiObjectiveFieldUnits();
    setAvailableUnits(units);
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      fetchUnits();
      if (isEditMode && prompt) {
        setPromptText(prompt.prompt || "");
        setSelectedSportId(prompt.master_sport_id);
        setObjectiveFields(
          prompt.objective_fields.length > 0
            ? prompt.objective_fields.map((f) => ({ ...f }))
            : [{ ...BLANK_OBJECTIVE_FIELD }]
        );
      } else {
        setPromptText("");
        setPromptCategory("sport");
        setSelectedSportId(null);
        setExerciseType("");
        setObjectiveFields([{ ...BLANK_OBJECTIVE_FIELD }]);
        fetchAvailableSports();
      }
    }
  }, [prompt, isEditMode, isModalVisible, fetchAvailableSports, fetchUnits]);

  const updateField = (
    index: number,
    key: keyof ObjectiveFieldType,
    value: string
  ) => {
    setObjectiveFields((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const addObjectiveField = () => {
    setObjectiveFields((prev) => [...prev, { ...BLANK_OBJECTIVE_FIELD }]);
  };

  const removeObjectiveField = (index: number) => {
    if (objectiveFields.length <= 1) return;
    setObjectiveFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!promptText.trim() || objectiveFields.length === 0) {
      return;
    }

    if (isEditMode) {
      if (!prompt) return;
      setIsSubmitting(true);
      try {
        await updateGeminiPrompt(prompt.id, {
          prompt: promptText,
          objective_fields: objectiveFields,
        });
        dispatch(setToastMessage([["Prompt updated successfully"], SUCCESS]));
        onUpdateSuccess();
        toggleModal();
      } catch (error) {
        // Error handling is done in the API interceptor
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (promptCategory === "sport" && !selectedSportId) return;
      if (promptCategory === "exercise" && !exerciseType.trim()) return;
      setIsSubmitting(true);
      try {
        await createGeminiPrompt(
          promptCategory === "sport"
            ? {
                prompt: promptText,
                prompt_category: "sport",
                master_sport_id: selectedSportId as number,
                objective_fields: objectiveFields,
              }
            : {
                prompt: promptText,
                prompt_category: "exercise",
                exercise_type: exerciseType.trim(),
                objective_fields: objectiveFields,
              }
        );
        dispatch(setToastMessage([["Prompt created successfully"], SUCCESS]));
        onUpdateSuccess();
        toggleModal();
      } catch (error) {
        // Error handling is done in the API interceptor
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (isEditMode && prompt) {
      setPromptText(prompt.prompt || "");
      setSelectedSportId(prompt.master_sport_id);
      setObjectiveFields(
        prompt.objective_fields.length > 0
          ? prompt.objective_fields.map((f) => ({ ...f }))
          : [{ ...BLANK_OBJECTIVE_FIELD }]
      );
    } else {
      setPromptText("");
      setPromptCategory("sport");
      setSelectedSportId(null);
      setExerciseType("");
      setObjectiveFields([{ ...BLANK_OBJECTIVE_FIELD }]);
    }
    toggleModal();
  };

  const isSubmitDisabled =
    isSubmitting ||
    !promptText.trim() ||
    objectiveFields.length === 0 ||
    (!isEditMode &&
      (promptCategory === "sport" ? !selectedSportId : !exerciseType.trim()));

  return (
    <Modal centered size="lg" show={isModalVisible} onHide={handleCancel}>
      <ModalHeader>
        {isEditMode ? "Edit Gemini Prompt" : "Create Gemini Prompt"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={{ overflowY: "auto", maxHeight: "70vh" }}>
          <Row>
            <Col sm={12}>
              {isEditMode && prompt?.prompt_category === "exercise" ? (
                <Form.Group controlId="exercise">
                  <Form.Label className="fs-xs">Exercise</Form.Label>
                  <Form.Control
                    type="text"
                    value={prompt.exercise_type ?? "----"}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              ) : isEditMode && prompt?.master_sport_id ? (
                <Form.Group controlId="sport">
                  <Form.Label className="fs-xs">Sport</Form.Label>
                  <Form.Control
                    type="text"
                    value={prompt.master_sport?.name || "----"}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              ) : !isEditMode ? (
                <>
                  <div className="mb-3">
                    <ToggleSwitch
                      textLeft="Sport"
                      textRight="Exercise"
                      checked={promptCategory === "exercise"}
                      onClick={() => {
                        const next =
                          promptCategory === "sport" ? "exercise" : "sport";
                        setPromptCategory(next);
                        setSelectedSportId(null);
                        setExerciseType("");
                        if (next === "sport") fetchAvailableSports();
                      }}
                    />
                  </div>
                  {promptCategory === "sport" ? (
                    <Form.Group controlId="sport">
                      <Form.Label className="fs-xs">Sport *</Form.Label>
                      <Form.Select
                        className="ss-select-control bg-white"
                        value={selectedSportId || ""}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setSelectedSportId(Number(e.target.value) || null)
                        }
                        required
                      >
                        <option value="" disabled>
                          Select Sport
                        </option>
                        {availableSports.map((sport) => (
                          <option key={sport.id} value={sport.id}>
                            {sport.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Form.Group controlId="exerciseType">
                      <Form.Label className="fs-xs">Exercise *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Dead Lift"
                        className="bg-white"
                        value={exerciseType}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setExerciseType(e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  )}
                </>
              ) : null}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={12}>
              <Form.Group controlId="prompt">
                <Form.Label className="fs-xs">Prompt *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  className="border"
                  placeholder="Enter prompt text"
                  value={promptText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setPromptText(e.target.value)
                  }
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm={12}>
              <Form.Label className="fs-xs fw-semibold">
                Objective Fields
              </Form.Label>
              {objectiveFields.map((field, index) => (
                <div
                  key={index}
                  className="sports-details-wrap p-3 mb-2 position-relative"
                >
                  {objectiveFields.length > 1 && (
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="fs-5 text-secondary position-absolute"
                      style={{
                        cursor: "pointer",
                        top: "0.75rem",
                        right: "0.75rem",
                      }}
                      onClick={() => removeObjectiveField(index)}
                    />
                  )}
                  <Row className="align-items-end">
                    <Col lg={4}>
                      <Form.Group controlId={`obj-title-${index}`}>
                        <Form.Label className="fs-xs">Title *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. total_shots"
                          className="bg-white"
                          value={field.title}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateField(index, "title", e.target.value)
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={4}>
                      <Form.Group controlId={`obj-unit-${index}`}>
                        <Form.Label className="fs-xs">Unit *</Form.Label>
                        <Form.Select
                          className="ss-select-control bg-white"
                          value={field.unit}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            updateField(index, "unit", e.target.value)
                          }
                          required
                        >
                          <option value="" disabled>
                            Select Unit
                          </option>
                          {availableUnits.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col lg={4}>
                      <Form.Group controlId={`obj-description-${index}`}>
                        <Form.Label className="fs-xs">Description *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Total shots taken"
                          className="bg-white"
                          value={field.description}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateField(index, "description", e.target.value)
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-1"
                onClick={addObjectiveField}
                type="button"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Objective Field
              </Button>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline-secondary"
            size="sm"
            className="px-4"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="px-4"
            type="submit"
            disabled={isSubmitDisabled}
          >
            {isEditMode ? "Save" : "Create"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditGeminiPromptModal;
